import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Store, Check, ArrowRight, Loader2, TrendingUp, Users, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "@/context/LocationContext";
import { ImageUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Footer } from "@/components/Footer";

export default function SellOnNabor() {
  const { user, refreshUser } = useAuth();
  const { setPostcode } = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingProfile, setExistingProfile] = useState(null);
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({ shop_name: "", description: "", category: "", logo: "", cover: "", area: "", postcode: "", lat: null, lng: null });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  useEffect(() => {
    let active = true;
    if (!user) { setExistingProfile(false); setChecking(false); return; }
    api.get("/seller/profile/me")
      .then((r) => { if (active) setExistingProfile(r.data); })
      .catch(() => { if (active) setExistingProfile(false); })
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    if (!checking && existingProfile === false && params.get("setup") === "1") setStarted(true);
  }, [checking, existingProfile, params]);

  const begin = () => {
    if (!user) { navigate("/auth"); return; }
    if (existingProfile) { navigate("/seller"); return; }
    setStarted(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let lat = null, lng = null;
      if (form.postcode) {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=gb&q=${encodeURIComponent(form.postcode)}`);
          const d = await res.json();
          if (d?.[0]) { lat = parseFloat(d[0].lat); lng = parseFloat(d[0].lon); }
        } catch {}
      }
      await api.post("/seller/profile", { ...form, lat, lng });
      await refreshUser();
      toast.success("Your shop is live! Add your first product.");
      navigate("/seller/products");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (!started) {
    return (
      <div>
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
          <div className="text-center max-w-2xl mx-auto animate-fade-up">
            <span className="bg-[#E07A5F]/10 text-[#E07A5F] px-3.5 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-2"><Sparkles size={13} /> Sell on Nabor</span>
            <h1 className="font-display text-3xl sm:text-5xl font-extrabold text-[#1A3C2A] mt-4 leading-tight">Turn your passion into a local shop</h1>
            <p className="text-base sm:text-lg text-[#5A626A] mt-4">Whether it's homemade bakes, fresh produce or handmade crafts — reach customers right in your neighbourhood.</p>
            <Button data-testid="sell-get-started" onClick={begin} className="mt-6 rounded-full bg-[#1A3C2A] hover:bg-[#132E20] h-12 px-7 font-semibold gap-2">Start Selling <ArrowRight size={16} /></Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-12">
            {[[Store, "Set up in minutes", "Create your storefront, add products and go live today."],
              [Users, "Reach local buyers", "Get discovered by shoppers searching in your area."],
              [TrendingUp, "Track your sales", "A simple dashboard for orders, earnings and reviews."]].map(([Icon, t, d], i) => (
              <div key={i} className="bg-white border border-[#E5DEC9] rounded-2xl p-5">
                <div className="w-11 h-11 rounded-xl bg-[#1A3C2A]/8 flex items-center justify-center text-[#1A3C2A]"><Icon size={22} /></div>
                <p className="font-display font-bold text-[#1A3C2A] mt-3">{t}</p>
                <p className="text-sm text-[#5A626A] mt-1">{d}</p>
              </div>
            ))}
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 min-h-[70vh]">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A] mb-1">Create your shop</h1>
      <p className="text-sm text-[#5A626A] mb-6">Tell shoppers about your business.</p>
      <form onSubmit={submit} className="bg-white border border-[#E5DEC9] rounded-2xl p-5 sm:p-6 space-y-5">
        <div className="flex gap-4">
          <div><Label className="text-xs mb-1 block">Logo</Label><ImageUpload testid="shop-logo" value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} label="Logo" /></div>
          <div className="flex-1"><Label className="text-xs mb-1 block">Cover photo</Label><ImageUpload testid="shop-cover" value={form.cover} onChange={(v) => setForm({ ...form, cover: v })} label="Cover" /></div>
        </div>
        <div><Label className="text-xs">Shop name</Label><Input data-testid="shop-name" value={form.shop_name} onChange={set("shop_name")} required className="rounded-xl mt-1" placeholder="The Corner Bakery" /></div>
        <div>
          <Label className="text-xs">Business category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="shop-category" className="rounded-xl mt-1"><SelectValue placeholder="Choose a category" /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Description</Label><Textarea data-testid="shop-description" value={form.description} onChange={set("description")} className="rounded-xl mt-1" placeholder="What do you make or sell?" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Area</Label><Input data-testid="shop-area" value={form.area} onChange={set("area")} className="rounded-xl mt-1" placeholder="Hackney, London" /></div>
          <div><Label className="text-xs">Postcode</Label><Input data-testid="shop-postcode" value={form.postcode} onChange={set("postcode")} className="rounded-xl mt-1" placeholder="E8 3DL" /></div>
        </div>
        <Button type="submit" data-testid="shop-create-submit" disabled={loading} className="w-full rounded-full bg-[#1A3C2A] hover:bg-[#132E20] h-12 font-semibold gap-2">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <><Check size={18} /> Open my shop</>}
        </Button>
      </form>
    </div>
  );
}
